import { useAccountTokenStore } from '@/entities/account-session';
import { useMyAppointments } from '@/entities/appointment';
import { useMyLabReviews } from '@/entities/lab-review';

/** A minute: a reply is worth seeing soon, and this runs on every patient screen. */
const POLL_MS = 60_000;

export interface ConsultReplies {
  /** How many answers on «مشاوره» the patient has not opened yet. */
  readonly count: number;
  /**
   * Which ones, as one string — so a notice dismissed for these answers comes
   * back for the next one, and only then.
   */
  readonly signature: string;
}

/**
 * «پاسخ تازه از پزشک» — the doctor's unread answers on «مشاوره»: a بررسی
 * آزمایش's conversation, or a phone consultation's «مدارک».
 *
 * Asked only with a session, and polled, so an answer shows up while the app is
 * open and not only on the next visit. The queries are the same ones «مشاوره»
 * reads, so opening a conversation there clears this in the same render.
 */
export function useConsultReplies(): ConsultReplies {
  const signedIn = useAccountTokenStore((state) => state.token !== null);
  const reviews = useMyLabReviews({ enabled: signedIn, refetchInterval: POLL_MS });
  const consultations = useMyAppointments({
    enabled: signedIn,
    kind: 'consultation',
    refetchInterval: POLL_MS,
  });

  if (!signedIn) {
    return { count: 0, signature: '' };
  }

  const ids = [
    ...(reviews.data ?? [])
      .filter((review) => review.hasUnseenReply)
      .map((review) => `r:${review.id}`),
    ...(consultations.data ?? [])
      .filter((consultation) => consultation.hasUnseenReply)
      .map((consultation) => `c:${consultation.id}`),
  ].sort();

  return { count: ids.length, signature: ids.join(',') };
}
